// components/BondResults.tsx
import React from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Menu,
  MenuItem
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  AccountBalance as BondIcon,
  FileDownload as DownloadIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Article as WordIcon,
  MoreVert as MoreIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { SimulationResult, BondForm } from '../simulationTypes';
import {
  formatCurrency,
  formatPercentage,
  exportToPDF,
  exportToExcel,
  exportToWord
} from '../exportCommon';

interface BondResultsProps {
  result: SimulationResult;
  form: BondForm;
  reportRef: React.RefObject<HTMLDivElement>;
  exportMenuAnchor: HTMLElement | null;
  setExportMenuAnchor: (anchor: HTMLElement | null) => void;
}

const BondResults: React.FC<BondResultsProps> = ({
  result,
  form,
  reportRef,
  exportMenuAnchor,
  setExportMenuAnchor
}) => {
  // Proper nested data access
  const asset = result.individual_assets[0];
  const analysis = asset.investment_performance_analysis;
  const summary = asset.asset_summary;
  const outcomes = asset.final_outcomes;
  const chartData = asset.distribution_data_for_charts;

  return (
    <Box ref={reportRef}>
      {/* Export Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight="bold" color="primary">
          Bond Investment Analysis Report
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          endIcon={<MoreIcon />}
          onClick={(e) => setExportMenuAnchor(e.currentTarget)}
        >
          Export
        </Button>
      </Box>

      {/* Simulation Summary */}
      <Card elevation={2} sx={{ mb: 3, bgcolor: 'primary.main', color: 'white' }}>
        <CardContent sx={{ p: { xs: 0.5, sm: 2 } }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <BondIcon sx={{ fontSize: 40 }} />
            </Grid>
            <Grid item xs>
              <Typography variant="h5" fontWeight="bold">
                {summary.asset_name}
              </Typography>
              <Typography variant="subtitle1">
                Simulation ID: {result.simulation_id} | Date:{' '}
                {new Date(result.simulation_date).toLocaleDateString()}
              </Typography>
              <Typography variant="body2">
                {summary.iterations_completed.toLocaleString()} iterations over{' '}
                {summary.investment_period_years} years
              </Typography>
              <Typography variant="body2">
                Initial Investment: ${summary.initial_investment.toLocaleString()}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Expected Returns P50 - Exact API mapping */}
      <Card elevation={1} sx={{ mb: 3, p: { xs: 0.5, sm: 2 } }}>
        <CardContent sx={{ p: { xs: 0.5, sm: 2 } }}>
          <Typography variant="h6" gutterBottom color="primary">
            📊 Expected Returns (P50)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: '#f5f5f5' }}>
                <Typography variant="caption" color="text.secondary">
                  Final Value
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {analysis.expected_returns_p50.final_value}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: '#f5f5f5' }}>
                <Typography variant="caption" color="text.secondary">
                  Total Profit
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="success.main">
                  {analysis.expected_returns_p50.total_profit}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: '#f5f5f5' }}>
                <Typography variant="caption" color="text.secondary">
                  Annual Return
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="success.main">
                  {analysis.expected_returns_p50.annual_return}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: '#f5f5f5' }}>
                <Typography variant="caption" color="text.secondary">
                  Break Even
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {analysis.expected_returns_p50.break_even}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Risk Assessment - Exact API mapping */}
      <Card elevation={1} sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 0.5, sm: 2 } }}>
          <Typography variant="h6" gutterBottom color="primary">
            ⚠️ Risk Assessment
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2">Probability of Profit</Typography>
                <Typography variant="body1" color="success.main" fontWeight="bold">
                  {analysis.risk_assessment.probability_of_profit}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2">Probability of Loss</Typography>
                <Typography variant="body1" color="success.main" fontWeight="bold">
                  {analysis.risk_assessment.probability_of_loss}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2">Best Case</Typography>
                <Typography variant="body1" color="success.main" fontWeight="bold">
                  {analysis.risk_assessment.best_case}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2">Worst Case</Typography>
                <Typography variant="body1" color="warning.main" fontWeight="bold">
                  {analysis.risk_assessment.worst_case}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Confidence Interval 80% */}
          <Box sx={{ mt: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              80% Confidence Interval
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Min Return:</strong>{' '}
                  {analysis.risk_assessment.confidence_interval_80?.min_return}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Max Return:</strong>{' '}
                  {analysis.risk_assessment.confidence_interval_80?.max_return}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* Charts - Using exact chart data from API */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Card elevation={1}>
            <CardContent sx={{ p: { xs: 0.5, sm: 2 } }}>
              <Typography variant="h6" gutterBottom>
                Bond Portfolio Value Over Time
              </Typography>
              <Chart
                options={
                  {
                    chart: { type: 'line', zoom: { enabled: true }, toolbar: { show: false } },
                    stroke: { curve: 'smooth', width: 3 },
                    xaxis: {
                      categories: chartData.asset_value_time_series.years.map(
                        (y: number) => `Year ${y + 1}`
                      ),
                      title: { text: 'Investment Years' }
                    },
                    yaxis: {
                      labels: { formatter: (val: number) => formatCurrency(val) },
                      title: { text: 'Bond Portfolio Value' }
                    },
                    legend: { position: 'top' },
                    colors: ['#f44336', '#2196f3', '#4caf50'],
                    tooltip: { y: { formatter: (val: number) => formatCurrency(val) } }
                  } as ApexOptions
                }
                series={[
                  {
                    name: 'Conservative (P10)',
                    data: chartData.asset_value_time_series.P10_values
                  },
                  { name: 'Expected (P50)', data: chartData.asset_value_time_series.P50_values },
                  { name: 'Optimistic (P90)', data: chartData.asset_value_time_series.P90_values }
                ]}
                type="line"
                height={350}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={1}>
            <CardContent sx={{ p: { xs: 0.5, sm: 2 } }}>
              <Typography variant="h6" gutterBottom>
                Annual Interest Income
              </Typography>
              <Chart
                options={
                  {
                    chart: { type: 'line', toolbar: { show: false } },
                    stroke: { curve: 'smooth', width: 2 },
                    xaxis: {
                      categories: chartData.cashflow_distribution.years.map(
                        (y: number) => `Year ${y}`
                      ),
                      title: { text: 'Years' }
                    },
                    yaxis: {
                      labels: { formatter: (val: number) => formatCurrency(val) },
                      title: { text: 'Interest Income' }
                    },
                    legend: { position: 'top' },
                    colors: ['#ff9800', '#03a9f4', '#8bc34a'],
                    tooltip: { y: { formatter: (val: number) => formatCurrency(val) } }
                  } as ApexOptions
                }
                series={[
                  {
                    name: 'Conservative (P10)',
                    data: chartData.cashflow_distribution.P10_cashflows
                  },
                  { name: 'Expected (P50)', data: chartData.cashflow_distribution.P50_cashflows },
                  { name: 'Optimistic (P90)', data: chartData.cashflow_distribution.P90_cashflows }
                ]}
                type="line"
                height={250}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={1}>
            <CardContent sx={{ p: { xs: 0.5, sm: 2 } }}>
              <Typography variant="h6" gutterBottom>
                Return Distribution
              </Typography>
              <Chart
                options={
                  {
                    chart: { type: 'bar', toolbar: { show: false } },
                    plotOptions: { bar: { borderRadius: 2 } },
                    xaxis: {
                      categories: chartData.histogram_final_returns.bins.map((bin: number) =>
                        formatCurrency(bin)
                      ),
                      labels: { rotate: -45 }
                    },
                    yaxis: { title: { text: 'Frequency' } },
                    colors: ['#9c27b0'],
                    dataLabels: { enabled: false }
                  } as ApexOptions
                }
                series={[
                  { name: 'Frequency', data: chartData.histogram_final_returns.frequencies }
                ]}
                type="bar"
                height={250}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Year by Year Insights - Exact API mapping */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" fontWeight="bold">
            📈 Year by Year Insights
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: '#f8f9fa', height: '100%' }}>
                <Typography variant="subtitle1" color="primary" fontWeight="bold" gutterBottom>
                  {analysis.year_by_year_insights.early_years.description}
                </Typography>
                {analysis.year_by_year_insights.early_years.key_characteristics.map(
                  (char: string, idx: number) => (
                    <Typography
                      key={idx}
                      variant="body2"
                      sx={{ mb: 1, display: 'flex', alignItems: 'flex-start' }}
                    >
                      <span style={{ marginRight: 8, color: '#1976d2' }}>•</span>
                      {char}
                    </Typography>
                  )
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: '#f1f8e9', height: '100%' }}>
                <Typography variant="subtitle1" color="success.main" fontWeight="bold" gutterBottom>
                  {analysis.year_by_year_insights.middle_period.description}
                </Typography>
                {analysis.year_by_year_insights.middle_period.key_characteristics.map(
                  (char: string, idx: number) => (
                    <Typography
                      key={idx}
                      variant="body2"
                      sx={{ mb: 1, display: 'flex', alignItems: 'flex-start' }}
                    >
                      <span style={{ marginRight: 8, color: '#4caf50' }}>•</span>
                      {char}
                    </Typography>
                  )
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: '#fff3e0', height: '100%' }}>
                <Typography variant="subtitle1" color="warning.main" fontWeight="bold" gutterBottom>
                  {analysis.year_by_year_insights.later_years.description}
                </Typography>
                {analysis.year_by_year_insights.later_years.key_characteristics.map(
                  (char: string, idx: number) => (
                    <Typography
                      key={idx}
                      variant="body2"
                      sx={{ mb: 1, display: 'flex', alignItems: 'flex-start' }}
                    >
                      <span style={{ marginRight: 8, color: '#ff9800' }}>•</span>
                      {char}
                    </Typography>
                  )
                )}
              </Paper>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Risk Distribution Analysis - Exact API mapping */}
      <Accordion sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" fontWeight="bold">
            🎯 Risk Distribution Analysis
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {/* Histogram Insights */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Histogram Insights
            </Typography>
            {analysis.risk_distribution_analysis?.histogram_insights?.map(
              (insight: string, idx: number) => (
                <Typography key={idx} variant="body2" sx={{ mb: 1 }}>
                  • {insight}
                </Typography>
              )
            )}
          </Box>

          {/* Outcome Clustering */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Outcome Clustering
            </Typography>
            <TableContainer component={Paper} elevation={1}>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <strong>Lower Quartile</strong>
                    </TableCell>
                    <TableCell align="right">
                      {analysis?.risk_distribution_analysis?.outcome_clustering?.lower_quartile}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Median</strong>
                    </TableCell>
                    <TableCell align="right">
                      {analysis?.risk_distribution_analysis?.outcome_clustering?.median}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Upper Quartile</strong>
                    </TableCell>
                    <TableCell align="right">
                      {analysis?.risk_distribution_analysis?.outcome_clustering?.upper_quartile}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Interquartile Range</strong>
                    </TableCell>
                    <TableCell align="right">
                      {
                        analysis?.risk_distribution_analysis?.outcome_clustering
                          ?.interquartile_range
                      }
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Volatility Analysis */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Volatility Analysis
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Card sx={{ textAlign: 'center', p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Early Years Volatility
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {
                      analysis?.risk_distribution_analysis?.volatility_analysis
                        ?.early_years_volatility
                    }
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{ textAlign: 'center', p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Later Years Volatility
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {
                      analysis?.risk_distribution_analysis?.volatility_analysis
                        ?.later_years_volatility
                    }
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{ textAlign: 'center', p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Volatility Trend
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {analysis?.risk_distribution_analysis?.volatility_analysis?.volatility_trend}
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Strategic Decision Points - Exact API mapping */}
      <Accordion sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" fontWeight="bold">
            ⚡ Strategic Decision Points
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: '#f8f9fa' }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Time Horizon Matters
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Short-term Risk:</strong>{' '}
                  {analysis?.strategic_decision_points?.time_horizon_matters?.short_term_risk}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Long-term Reward:</strong>{' '}
                  {analysis?.strategic_decision_points?.time_horizon_matters?.long_term_reward}
                </Typography>
                <Typography variant="body2">
                  <strong>Patience Benefit:</strong>{' '}
                  {analysis?.strategic_decision_points?.time_horizon_matters?.patience_benefit}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: '#f1f8e9' }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Risk vs Reward Profile
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Conservative:</strong>{' '}
                  {
                    analysis?.strategic_decision_points?.risk_vs_reward_profile
                      ?.conservative_estimate
                  }
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Realistic:</strong>{' '}
                  {
                    analysis?.strategic_decision_points?.risk_vs_reward_profile
                      ?.realistic_expectation
                  }
                </Typography>
                <Typography variant="body2">
                  <strong>Optimistic:</strong>{' '}
                  {analysis?.strategic_decision_points?.risk_vs_reward_profile?.optimistic_scenario}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: '#fff3e0' }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Market Timing Insights
                </Typography>
                {analysis?.strategic_decision_points?.market_timing_insights?.map(
                  (insight: string, idx: number) => (
                    <Typography key={idx} variant="body2" sx={{ mb: 1 }}>
                      • {insight}
                    </Typography>
                  )
                )}
              </Paper>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Comparison Benchmarks - Exact API mapping */}
      <Card elevation={2} sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
            📊 Comparison Benchmarks
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Chip
                label={`vs Term Deposits: ${analysis.comparison_benchmarks.against_alternatives.term_deposits}`}
                color="info"
                variant="outlined"
                sx={{ width: '100%', justifyContent: 'flex-start' }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Chip
                label={`vs High Grade Bonds: ${analysis.comparison_benchmarks.against_alternatives.high_grade_bonds}`}
                color="info"
                variant="outlined"
                sx={{ width: '100%', justifyContent: 'flex-start' }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Chip
                label={`vs Stock Market: ${analysis.comparison_benchmarks.against_alternatives.stock_market}`}
                color="info"
                variant="outlined"
                sx={{ width: '100%', justifyContent: 'flex-start' }}
              />
            </Grid>
          </Grid>
          <Typography variant="body2" sx={{ mt: 2 }}>
            <strong>Risk-Adjusted Assessment:</strong>{' '}
            {analysis.comparison_benchmarks.risk_adjusted_assessment}
          </Typography>
        </CardContent>
      </Card>

      {/* Actionable Investment Decisions - Exact API mapping */}
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12} md={6}>
          <Card elevation={2} sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" color="success.main" fontWeight="bold" gutterBottom>
                ✅ Green Lights
              </Typography>
              {analysis.actionable_investment_decisions.green_lights.map(
                (item: string, idx: number) => (
                  <Typography
                    key={idx}
                    variant="body2"
                    sx={{ mb: 1.5, display: 'flex', alignItems: 'flex-start' }}
                  >
                    <CheckCircleIcon sx={{ color: 'success.main', mr: 1, mt: 0.2, fontSize: 16 }} />
                    {item}
                  </Typography>
                )
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card elevation={2} sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" color="warning.main" fontWeight="bold" gutterBottom>
                ⚠️ Red Flags to Monitor
              </Typography>
              {analysis.actionable_investment_decisions.red_flags_to_monitor.map(
                (item: string, idx: number) => (
                  <Typography
                    key={idx}
                    variant="body2"
                    sx={{ mb: 1.5, display: 'flex', alignItems: 'flex-start' }}
                  >
                    <WarningIcon sx={{ color: 'warning.main', mr: 1, mt: 0.2, fontSize: 16 }} />
                    {item}
                  </Typography>
                )
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Portfolio Strategy Implications - Exact API mapping */}
      <Accordion sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" fontWeight="bold">
            🎯 Portfolio Strategy Implications
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Optimal Investor Profile
              </Typography>
              {analysis?.portfolio_strategy_implications?.optimal_investor_profile.map(
                (profile: string, idx: number) => (
                  <Typography key={idx} variant="body2" sx={{ mb: 1 }}>
                    • {profile}
                  </Typography>
                )
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Position Sizing Recommendation
              </Typography>
              <Typography variant="body2">
                {analysis?.portfolio_strategy_implications?.position_sizing_recommendation}
              </Typography>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Key Takeaway - CORRECTED: Proper nested access */}
      <Card
        elevation={3}
        sx={{
          mt: 3,
          background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
          color: 'white'
        }}
      >
        <CardContent>
          <Typography variant="h5" sx={{ color: 'white', mb: 2, fontWeight: 'bold' }}>
            💡 Key Bond Investment Takeaway
          </Typography>
          <Typography variant="body1" sx={{ color: 'white', lineHeight: 1.6 }}>
            {asset?.investment_performance_analysis?.key_takeaway || 'No data available.'}
          </Typography>
        </CardContent>
      </Card>

      {/* Final Outcomes - Exact API mapping */}
      <Accordion sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" fontWeight="bold">
            📊 Final Outcomes
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Metric</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Conservative (P10)</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Expected (P50)</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Optimistic (P90)</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Total Asset Value</TableCell>
                  <TableCell align="right">
                    {formatCurrency(outcomes.total_asset_value.P10)}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(outcomes.total_asset_value.P50)}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(outcomes.total_asset_value.P90)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Total Net Profit</TableCell>
                  <TableCell align="right" sx={{ color: 'success.main' }}>
                    {formatCurrency(outcomes.total_net_profit.P10)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'success.main' }}>
                    {formatCurrency(outcomes.total_net_profit.P50)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'success.main' }}>
                    {formatCurrency(outcomes.total_net_profit.P90)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Annual IRR Percent</TableCell>
                  <TableCell align="right">
                    {formatPercentage(outcomes.annual_irr_percent.P10)}
                  </TableCell>
                  <TableCell align="right">
                    {formatPercentage(outcomes.annual_irr_percent.P50)}
                  </TableCell>
                  <TableCell align="right">
                    {formatPercentage(outcomes.annual_irr_percent.P90)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>ROI Percent</TableCell>
                  <TableCell align="right">{formatPercentage(outcomes.roi_percent.P10)}</TableCell>
                  <TableCell align="right">{formatPercentage(outcomes.roi_percent.P50)}</TableCell>
                  <TableCell align="right">{formatPercentage(outcomes.roi_percent.P90)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* Yearly Projections - Exact API mapping */}
      <Accordion sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" fontWeight="bold">
            📅 Yearly Projections ({form.years} Years)
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Year</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Asset Value (P50)</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Annual Income (P50)</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Annual Expenses (P50)</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Net Cashflow (P50)</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Cumulative Profit (P50)</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {asset.yearly_projections.map((projection: any, idx: number) => (
                  <TableRow key={idx}>
                    <TableCell>{projection.year}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(projection.asset_value.P50)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(projection.annual_income.P50)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(projection.annual_expenses.P50)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'success.main' }}>
                      {formatCurrency(projection.net_cashflow.P50)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color: projection.cumulative_profit.P50 < 0 ? 'error.main' : 'success.main'
                      }}
                    >
                      {formatCurrency(projection.cumulative_profit.P50)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* Risk Metrics - Exact API mapping */}
      <Accordion sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" fontWeight="bold">
            📈 Risk Metrics
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Value at Risk 95%
                </Typography>
                <Typography variant="h6">
                  {formatCurrency(chartData?.risk_metrics?.value_at_risk_95 || 0)}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Expected Shortfall
                </Typography>
                <Typography variant="h6">
                  {formatCurrency(chartData?.risk_metrics?.expected_shortfall || 0)}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Standard Deviation
                </Typography>
                <Typography variant="h6">
                  {formatCurrency(chartData?.risk_metrics?.standard_deviation || 0)}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Skewness
                </Typography>
                <Typography variant="h6">
                  {chartData?.risk_metrics?.skewness?.toFixed(4)}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Kurtosis
                </Typography>
                <Typography variant="h6">
                  {chartData?.risk_metrics?.kurtosis?.toFixed(4)}
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Export Menu */}
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={() => setExportMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => exportToPDF(result, reportRef, form.assetName, setExportMenuAnchor)}
        >
          <PdfIcon sx={{ mr: 1 }} /> Export as PDF
        </MenuItem>
        <MenuItem onClick={() => exportToExcel(result, form, setExportMenuAnchor, 'bond')}>
          <ExcelIcon sx={{ mr: 1 }} /> Export as Excel
        </MenuItem>
        <MenuItem onClick={() => exportToWord(result, form, setExportMenuAnchor, 'bond')}>
          <WordIcon sx={{ mr: 1 }} /> Export as Word
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default BondResults;
